import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, MousePointerClick, Plus, Sparkles, Upload, UserRound, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAnalyticsApi } from '../../../common/hooks/use-analytics-api.hook';
import { Lead, useLeadsApi } from '../../../common/hooks/use-leads-api.hook';
import {
  BarList,
  PeriodControl,
  PeriodValue,
  TrendChart,
  defaultGranularity,
  fitPeriod,
  resolvePeriod,
  utcDay,
} from '../../../components/design-system/analytics';
import { HistoryLimitNotice, useHistoryWindow } from '../../../components/design-system/plan/HistoryLimitNotice';
import { PlanMeters } from '../../../components/design-system/plan/PlanMeters';
import { KpiCard, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { ActivityFeed } from '../../../modules/timeline/activity/ActivityFeed';
import { buildSessions } from '../../../modules/timeline/activity/activity-feed';
import { VizHeader } from '../../../modules/vendor/analytics/viz/components/VizHeader';
import { LiveNow } from '../../../modules/vendor/analytics/viz/components/LiveNow';
import { NoTrafficState } from '../../../modules/vendor/analytics/viz/components/NoTrafficState';
import { ReadingCard } from '../../../modules/vendor/analytics/viz/components/ReadingCard';
import { VizFunnel } from '../../../modules/vendor/analytics/viz/components/VizFunnel';
import { TopProducts } from '../../../modules/vendor/analytics/viz/components/TopProducts';
import { LeadsWaiting } from '../../../modules/vendor/analytics/viz/components/LeadsWaiting';
import { LeadReading, readLead, rankLeads } from '../../../modules/vendor/analytics/viz/lead-temperature';
import { toProductRows, triedNotSaved, sortProducts } from '../../../modules/vendor/analytics/viz/product-performance';
import { vizReading } from '../../../modules/vendor/analytics/viz/viz-reading';
import { deltaOf, noTraffic, pairOf, sparkOf, trendOf, valueOf } from '../../../modules/vendor/analytics/viz/metrics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { VIZ_LINKS } from '../../../modules/vendor/analytics/viz/links';

/**
 * Catalog size below which the dashboard still offers the product-creation
 * shortcuts. They are onboarding scaffolding: a vendor with a stocked catalog
 * knows where products live.
 */
const ONBOARDING_CATALOG_THRESHOLD = 10;

/**
 * The visualizer at a glance: did people use it, is that growing, which
 * products work, where people leave, and who to call. The reading says it in
 * sentences; the cards and charts below are the evidence.
 */
export const OverviewPage = () => {
  const { t, i18n } = useTranslation();
  const analytics = useAnalyticsApi();
  const leadsApi = useLeadsApi();
  const [range, setRange] = useState<PeriodValue>({ key: '30d' });
  const { maxDays } = useHistoryWindow();
  const shown = useMemo(() => fitPeriod(range, maxDays) as PeriodValue, [range, maxDays]);

  const period = useMemo(() => resolvePeriod(shown.key, { start: shown.start, end: shown.end }), [shown]);
  const granularity = defaultGranularity(period.days);
  const locale = (i18n.language || 'en').slice(0, 2);

  const { data, loading, error, reload } = useLoader(async () => {
    const { startDate, endDate } = period;
    const metric = (name: string, extra: Record<string, unknown> = {}) =>
      analytics.queryMetric({ metric: name, startDate, endDate, compareToPreviousPeriod: true, ...extra });
    const series = { groupBy: 'time' as const, granularity };

    const [visitors, tries, downloads, leads, trialVisitors, resultVisitors, zeroSearches, countries, products, leadsPage, timeline, catalog] =
      await Promise.all([
        metric('unique_visitors', series),
        metric('product_applications', series),
        metric('result_downloads', series),
        metric('new_leads', series),
        metric('product_trial_visitors'),
        metric('result_visitors'),
        metric('zero_result_searches'),
        analytics.queryMetric({ metric: 'unique_visitors', startDate, endDate, groupBy: 'country', limit: 6 }),
        analytics.getProductAnalytics({ dateRange: { startDate, endDate } }, 60).catch(() => null),
        leadsApi.findAllLeads({ limit: 200, sortBy: 'lastSeenAt', sortOrder: 'desc' }).catch(() => null),
        analytics.fetchTimelineEvents({ limit: 40, sortOrder: 'desc' }).catch(() => null),
        analytics.getCatalogProducts(1),
      ]);
    return { visitors, tries, downloads, leads, trialVisitors, resultVisitors, zeroSearches, countries, products, leadsPage, timeline, catalog };
  }, [period.startDate, period.endDate, granularity]);

  const productRows = useMemo(() => toProductRows(data?.products?.data?.products), [data?.products]);
  const leadList = useMemo<Lead[]>(() => data?.leadsPage?.data ?? [], [data?.leadsPage]);
  const readings = useMemo(() => new Map<string, LeadReading>(leadList.map((l) => [l._id, readLead(l)])), [leadList]);
  const rankedLeads = useMemo(() => rankLeads(leadList, readings), [leadList, readings]);
  const sessions = useMemo(() => buildSessions((data?.timeline?.data as any[]) ?? []), [data?.timeline]);

  const reading = useMemo(() => {
    if (!data) return [];
    const waiting = rankedLeads.filter((l) => ['email_now', 'follow_up'].includes(readings.get(l._id)!.action));
    const weak = triedNotSaved(productRows, { limit: 1 })[0];
    const top = sortProducts(productRows, 'tries')[0];
    return vizReading({
      visitors: pairOf(data.visitors),
      tries: pairOf(data.tries),
      downloads: pairOf(data.downloads),
      leads: pairOf(data.leads),
      trialVisitors: valueOf(data.trialVisitors),
      resultVisitors: valueOf(data.resultVisitors),
      leadsWaiting: waiting.length,
      hotWaiting: waiting.filter((l) => readings.get(l._id)!.temperature === 'hot').length,
      weakProduct: weak ? { name: weak.name, tries: weak.tries, downloads: weak.downloads } : null,
      topProduct: top && top.tries > 0 ? { name: top.name, tries: top.tries } : null,
      zeroResultSearches: { count: valueOf(data.zeroSearches) ?? 0 },
    });
  }, [data, productRows, rankedLeads, readings]);

  const empty = !loading && !!data && data.visitors !== null && noTraffic(data.visitors);
  const failed = !loading && (error || (data && data.visitors === null));
  const smallCatalog = !loading && !!data && data.catalog.total < ONBOARDING_CATALOG_THRESHOLD;
  const vsPrevious = t('VIZ_ANALYTICS.COMMON.VS_PREVIOUS', { count: period.days });

  return (
    <PageContainer breadcrumbs={[{ label: t('SIDEBAR.VISUALIZER.TITLE') }, { label: t('SIDEBAR.DASHBOARD.TITLE') }]}>
      <div className="mx-auto flex max-w-[1200px] flex-col gap-6" data-testid="viz-dashboard">
        <VizHeader
          title={t('VIZ_ANALYTICS.DASHBOARD.TITLE')}
          description={t('VIZ_ANALYTICS.DASHBOARD.SUBTITLE')}
          actions={
            <>
              <LiveNow />
              <PeriodControl value={shown} onChange={setRange} maxDays={maxDays} />
            </>
          }
        />

        <HistoryLimitNotice period={period} />
        {/* What the plan allows this month — only drawn when the plan meters it. */}
        <PlanMeters keys={['maxVisualizationsPerMonth', 'maxRoomAnalysesPerMonth']} testId="viz-plan-meters" />

        {failed && (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.LOAD_FAILED')}</p>
              <Button variant="outline" size="sm" onClick={reload}>
                {t('VIZ_ANALYTICS.COMMON.RETRY')}
              </Button>
            </CardContent>
          </Card>
        )}

        {empty ? (
          <NoTrafficState />
        ) : (
          !failed && (
            <>
              <ReadingCard items={reading} loading={loading} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                  testId="viz-kpi-visitors"
                  label={t('VIZ_ANALYTICS.KPI.VISITORS')}
                  value={formatNumber(valueOf(data?.visitors))}
                  delta={deltaOf(data?.visitors)}
                  hint={vsPrevious}
                  icon={<Users />}
                  trend={data ? sparkOf(data.visitors, period, granularity) : undefined}
                  href={VIZ_LINKS.analytics}
                  loading={loading}
                />
                <KpiCard
                  testId="viz-kpi-tries"
                  label={t('VIZ_ANALYTICS.KPI.TRIES')}
                  value={formatNumber(valueOf(data?.tries))}
                  delta={deltaOf(data?.tries)}
                  hint={vsPrevious}
                  icon={<MousePointerClick />}
                  trend={data ? sparkOf(data.tries, period, granularity) : undefined}
                  href={VIZ_LINKS.products}
                  loading={loading}
                />
                <KpiCard
                  testId="viz-kpi-downloads"
                  label={t('VIZ_ANALYTICS.KPI.DOWNLOADS')}
                  value={formatNumber(valueOf(data?.downloads))}
                  delta={deltaOf(data?.downloads)}
                  hint={vsPrevious}
                  icon={<Download />}
                  trend={data ? sparkOf(data.downloads, period, granularity) : undefined}
                  href={VIZ_LINKS.analytics}
                  loading={loading}
                />
                <KpiCard
                  testId="viz-kpi-leads"
                  label={t('VIZ_ANALYTICS.KPI.LEADS')}
                  value={formatNumber(valueOf(data?.leads))}
                  delta={deltaOf(data?.leads)}
                  hint={vsPrevious}
                  icon={<UserRound />}
                  trend={data ? sparkOf(data.leads, period, granularity) : undefined}
                  href={VIZ_LINKS.leads}
                  loading={loading}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                <SectionCard
                  className="min-w-0 lg:col-span-3"
                  title={t('VIZ_ANALYTICS.TREND.VISITORS_TITLE')}
                  description={t(`VIZ_ANALYTICS.TREND.DESCRIPTION_${granularity.toUpperCase()}`)}
                  testId="viz-visitors-trend"
                >
                  <TrendChart
                    data={data ? trendOf(data.visitors, period, granularity, locale) : []}
                    valueLabel={t('VIZ_ANALYTICS.TREND.THIS_PERIOD')}
                    previousLabel={t('VIZ_ANALYTICS.TREND.PREVIOUS_PERIOD')}
          // The period runs to today: the last bucket is still filling up.
          partialLast={period.endDate >= utcDay(Date.now())}
          partialLabel={t('VIZ_ANALYTICS.TREND.SO_FAR')}
                    height={240}
                  />
                </SectionCard>
                <VizFunnel
                  className="min-w-0 lg:col-span-2"
                  loading={loading}
                  counts={{
                    visitors: valueOf(data?.visitors),
                    tried: valueOf(data?.trialVisitors),
                    saved: valueOf(data?.resultVisitors),
                    leads: valueOf(data?.leads),
                  }}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <TopProducts rows={productRows} loading={loading} className="min-w-0" />
                <LeadsWaiting leads={rankedLeads} readings={readings} loading={loading} className="min-w-0" />
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                <SectionCard
                  className="min-w-0 lg:col-span-3"
                  title={t('VIZ_ANALYTICS.ACTIVITY.RECENT_TITLE')}
                  description={t('VIZ_ANALYTICS.ACTIVITY.RECENT_DESCRIPTION')}
                  action={
                    <Link to={VIZ_LINKS.activity} className="text-xs text-muted-foreground hover:text-foreground">
                      {t('VIZ_ANALYTICS.COMMON.SEE_ALL')}
                    </Link>
                  }
                  testId="viz-recent-activity"
                >
                  <ActivityFeed sessions={sessions} loading={loading} compact limit={6} />
                </SectionCard>
                <SectionCard
                  className="min-w-0 lg:col-span-2"
                  title={t('VIZ_ANALYTICS.GEO.TITLE')}
                  description={t('VIZ_ANALYTICS.GEO.DESCRIPTION')}
                  testId="viz-countries"
                >
                  <BarList
                    emptyText={t('VIZ_ANALYTICS.GEO.EMPTY')}
                    items={(data?.countries?.breakdown ?? []).map((row) => ({ key: row.key, label: row.key, value: row.value }))}
                  />
                </SectionCard>
              </div>
            </>
          )
        )}

        {smallCatalog && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="viz-onboarding">
            <OnboardingLink to={VIZ_LINKS.newProduct} icon={<Plus />} title={t('VIZ_ANALYTICS.ONBOARDING.ADD_TITLE')} text={t('VIZ_ANALYTICS.ONBOARDING.ADD_TEXT')} />
            <OnboardingLink to={VIZ_LINKS.importProducts} icon={<Upload />} title={t('VIZ_ANALYTICS.ONBOARDING.IMPORT_TITLE')} text={t('VIZ_ANALYTICS.ONBOARDING.IMPORT_TEXT')} />
            <OnboardingLink to={VIZ_LINKS.newProduct} state={{ aiMode: true }} icon={<Sparkles />} title={t('VIZ_ANALYTICS.ONBOARDING.AI_TITLE')} text={t('VIZ_ANALYTICS.ONBOARDING.AI_TEXT')} />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

function OnboardingLink({ to, state, icon, title, text }: { to: string; state?: unknown; icon: ReactNode; title: string; text: string }) {
  return (
    <Link
      to={to}
      state={state}
      className="flex items-start gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground [&_svg]:size-4">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{text}</span>
      </span>
    </Link>
  );
}

export default OverviewPage;
